/**
 * Custom search ATC error banner.
 * Lives outside Horizon's `predictive-search.js` so theme updates do not overwrite it.
 * Listens on `document` because search results are morphed in and out.
 */
import { ThemeEvents } from '@theme/events';

const SEARCH_ATC_ERROR_DISPLAY_DURATION = 10000;

/** @type {number | undefined} */
let searchAtcErrorTimeout;

/**
 * @param {Element} from
 * @returns {HTMLElement | null}
 */
function getSearchAtcErrorBanner(from) {
  const list = from.closest('.predictive-search-results__wrapper-products');
  const sibling = list?.previousElementSibling;
  if (sibling instanceof HTMLElement && sibling.hasAttribute('data-search-atc-error')) {
    return sibling;
  }
  return null;
}

/**
 * @param {Element} from
 * @returns {HTMLElement | null}
 */
function getPredictiveSearchRoot(from) {
  return from.closest('predictive-search-component');
}

/**
 * @param {Element} from
 */
function resetSearchAtcSuccessIcon(from) {
  const form = from.closest('product-form-component') ?? from;
  form.querySelectorAll('[data-added="true"]').forEach((button) => {
    button.removeAttribute('data-added');
  });
}

function clearSearchAtcErrorTimeout() {
  if (searchAtcErrorTimeout) {
    clearTimeout(searchAtcErrorTimeout);
    searchAtcErrorTimeout = undefined;
  }
}

/**
 * @param {HTMLElement} banner
 */
function setSearchAtcErrorBannerHidden(banner) {
  banner.hidden = true;
  banner.classList.add('hidden');
  const text = banner.querySelector('[data-search-atc-error-text]');
  if (text) text.textContent = '';
}

/**
 * @param {Element} [from]
 */
function hideSearchAtcError(from) {
  clearSearchAtcErrorTimeout();

  if (from) {
    const banner = getSearchAtcErrorBanner(from);
    if (banner) {
      setSearchAtcErrorBannerHidden(banner);
      return;
    }

    const root = getPredictiveSearchRoot(from);
    root?.querySelectorAll('[data-search-atc-error]').forEach((banner) => {
      if (banner instanceof HTMLElement) setSearchAtcErrorBannerHidden(banner);
    });
    return;
  }

  document.querySelectorAll('[data-search-atc-error]').forEach((banner) => {
    if (banner instanceof HTMLElement) setSearchAtcErrorBannerHidden(banner);
  });
}

/**
 * @param {Element} from
 * @param {string} message
 */
function showSearchAtcError(from, message) {
  const banner = getSearchAtcErrorBanner(from);
  if (!banner) return;

  const text = banner.querySelector('[data-search-atc-error-text]');
  if (text) text.textContent = message;

  banner.hidden = false;
  banner.classList.remove('hidden');
  resetSearchAtcSuccessIcon(from);

  clearSearchAtcErrorTimeout();
  searchAtcErrorTimeout = setTimeout(() => {
    hideSearchAtcError(from);
  }, SEARCH_ATC_ERROR_DISPLAY_DURATION);
}

/**
 * Builds the min/max quantity error message for a search ATC form, matching
 * the wording used on the product page/PLP.
 * @param {HTMLElement} form
 * @param {'min'|'max'} reason
 * @param {number|null} [limit]
 * @returns {string}
 */
function buildQuantityLimitMessage(form, reason, limit) {
  const isMinError = reason === 'min';
  const template = (isMinError ? form.dataset.quantityErrorMin : form.dataset.quantityErrorMax) || '';
  return template
    .replace('{{ limit }}', limit?.toString() || '')
    .replace('{{ product }}', form.dataset.productTitle || '');
}

/**
 * @param {Event} event
 */
function onSearchAtcSubmit(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const form = target.closest('product-form-component.custom-search-atc');
  if (!form) return;

  const quantitySelector = /** @type {{ canAddToCart?: () => { canAdd: boolean, reason: 'min'|'max'|null, minQuantity: number, maxQuantity: number | null } } | null} */ (
    form.querySelector('quantity-selector-component')
  );
  if (!quantitySelector?.canAddToCart) return;

  const validation = quantitySelector.canAddToCart();
  if (validation.canAdd) return;

  const limit = validation.reason === 'min' ? validation.minQuantity : validation.maxQuantity;
  showSearchAtcError(form, buildQuantityLimitMessage(form, validation.reason, limit));
}

/**
 * Shows the min/max error when a search card's +/- button is clicked past
 * the limit, without waiting for a submit attempt.
 * @param {Event & { detail?: { reason?: 'min'|'max', limit?: number|null } }} event
 */
function onSearchQuantityBoundary(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const form = target.closest('product-form-component.custom-search-atc');
  const { reason, limit } = event.detail || {};
  if (!form || !reason) return;

  showSearchAtcError(form, buildQuantityLimitMessage(form, reason, limit));
}

/**
 * @param {Event & { detail?: { data?: { message?: string } } }} event
 */
function onSearchCartError(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement) || !target.closest('.custom-search-atc')) return;

  const message = event.detail?.data?.message;
  if (!message) return;

  showSearchAtcError(target, message);
}

/**
 * @param {Event & { detail?: { data?: { didError?: boolean } } }} event
 */
function onSearchCartUpdate(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement) || !target.closest('.custom-search-atc')) return;
  if (event.detail?.data?.didError) return;

  hideSearchAtcError(target);
}

/**
 * @param {Event} event
 */
function onSearchInput(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (!target.matches('input[type="search"]')) return;
  if (!target.closest('predictive-search-component')) return;

  hideSearchAtcError(target);
}

/**
 * @param {Event} event
 */
function onSearchClick(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const clearOrReset = target.closest(
    '.predictive-search-results__clear, .predictive-search__reset-button'
  );
  if (!clearOrReset) return;

  hideSearchAtcError(clearOrReset);
}

document.addEventListener(ThemeEvents.cartError, onSearchCartError);
document.addEventListener(ThemeEvents.cartUpdate, onSearchCartUpdate);
document.addEventListener(ThemeEvents.quantitySelectorBoundary, onSearchQuantityBoundary);
document.addEventListener('submit', onSearchAtcSubmit);
document.addEventListener('input', onSearchInput);
document.addEventListener('click', onSearchClick);
